import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
  FormHelperText
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../../../components/CustomButton/Button";
import axios from "axios";

interface BusinessOption {
  id: string;
  name: string;
}

interface AddPointsDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  businesses: BusinessOption[];
  onSuccess?: () => void;
}

const apiEndpoint = process.env.REACT_APP_API_BASE_URL;

const AddPointsDialog: React.FC<AddPointsDialogProps> = ({
  open,
  onClose,
  userId,
  businesses,
  onSuccess
}) => {
  const [businessId, setBusinessId] = React.useState("");
  const [points, setPoints] = React.useState("");
  const [validationError, setValidationError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const validateBusinessId = (id: string) => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(id)) {
      return "Please enter a valid Business ID (UUID format)";
    }
    return "";
  };

  const validatePoints = (pointsValue: string) => {
    const pointsNum = parseInt(pointsValue);
    if (isNaN(pointsNum) || pointsNum === 0) {
      return "Please enter a valid non-zero number for points";
    }
    if (pointsNum > 1000000 || pointsNum < -1000000) {
      return "Points cannot exceed ±1,000,000";
    }
    return "";
  };

  const handleConfirm = async () => {
    const trimmedBusinessId = businessId.trim();
    const trimmedPoints = points.trim();

    if (!trimmedBusinessId) {
      setValidationError("Business ID is required");
      return;
    }

    if (!trimmedPoints) {
      setValidationError("Points are required");
      return;
    }

    const businessIdError = validateBusinessId(trimmedBusinessId);
    if (businessIdError) {
      setValidationError(businessIdError);
      return;
    }

    const pointsError = validatePoints(trimmedPoints);
    if (pointsError) {
      setValidationError(pointsError);
      return;
    }

    setValidationError("");
    setError("");
    setLoading(true);

    try {
      await axios.post(
        `${apiEndpoint}/loyaltyprogram/support/user/points`,
        {
          UserId: userId,
          BusinessId: trimmedBusinessId,
          Points: parseInt(trimmedPoints)
        },
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      setBusinessId("");
      setPoints("");
      setLoading(false);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        "Failed to add points. Please try again.";
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setBusinessId("");
    setPoints("");
    setValidationError("");
    setError("");
    setLoading(false);
    onClose();
  };

  const handleBusinessIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBusinessId(e.target.value);
    if (validationError) {
      setValidationError("");
    }
    if (error) {
      setError("");
    }
  };

  const handlePointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPoints(e.target.value);
    if (validationError) {
      setValidationError("");
    }
    if (error) {
      setError("");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
            Add Points
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {(error || validationError) && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
            {error || validationError}
          </Alert>
        )}
        <Typography sx={{ mb: 2 }}>
          Enter the Business ID and number of points to add for this user.
        </Typography>

        <TextField
          fullWidth
          label="Business ID"
          value={businessId}
          onChange={handleBusinessIdChange}
          disabled={loading}
          placeholder="Enter Business ID (e.g., b02d3127-1490-4341-ac71-5c8ea27b0dd0)"
          error={!!validationError && !businessId.trim()}
          sx={{ mt: 1 }}
        />
        <FormHelperText sx={{ color: "text.secondary" }}>
          Business ID Format: UUID (e.g., xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
        </FormHelperText>

        <TextField
          fullWidth
          label="Points"
          type="number"
          value={points}
          onChange={handlePointsChange}
          disabled={loading}
          placeholder="Enter number of points (e.g., 20)"
          error={!!validationError && !points.trim()}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <CustomButton callBack={handleClose} disabled={loading}>
          Cancel
        </CustomButton>
        <CustomButton
          callBack={handleConfirm}
          disabled={loading || !businessId.trim() || !points.trim()}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Add Points"
          )}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddPointsDialog;
